import { observer } from 'mobx-react-lite';
import { useIntl } from 'react-intl';
import { useMessagingStore } from '../hooks/useMessagingStore.tsx';
import { SurfaceRenderer } from '../a2ui/SurfaceRenderer.tsx';
import { messages } from '../localization/messages.ts';
import { Button } from '../shadcdn/button.tsx';
import { TextPage } from './TextPage.tsx';
import { processPageFor } from './process/index.ts';
import type { LoadingLine } from './loading-line.ts';
import type { StageView } from './stage-view.ts';
import type { buildTurnIndex } from './turn-index.ts';
import type { ReactNode } from 'react';

/** Blocking arrival failures stay visible without opening activity details. */
function ArrivalNotice({ line, onRetry }: { line: LoadingLine | null; onRetry: () => void }) {
  const intl = useIntl();
  if (!line || line.kind === 'connecting' || line.kind === 'preparing' || line.kind === 'working') {
    return null;
  }
  return (
    <div className="flex min-h-[40vh] min-w-0 flex-col items-center justify-center gap-2 px-4 text-center">
      <p aria-live="polite" className="w-full max-w-md wrap-anywhere text-sm text-muted-foreground">
        <span
          key={line.text}
          className="animate-in fade-in duration-500 motion-reduce:animate-none"
        >
          {line.text}
        </span>
      </p>
      {'retry' in line && line.retry ? (
        <Button type="button" variant="link" size="surface" onClick={onRetry} className="underline">
          {intl.formatMessage(messages.retry)}
        </Button>
      ) : null}
    </div>
  );
}

function UserRequestBubble({ request }: { request: string }) {
  if (!request) {
    return null;
  }
  return (
    <div className="mb-6 flex w-full justify-end" data-stage-user-message>
      <p
        className="min-w-0 max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm border border-primary/30 bg-primary/10 px-4 py-3 text-sm leading-relaxed text-foreground wrap-anywhere sm:max-w-[80%]"
        dir="auto"
      >
        {request}
      </p>
    </div>
  );
}

interface StagePageProps {
  view: StageView;
  turns: ReturnType<typeof buildTurnIndex>;
  request: string;
  loadingLine: LoadingLine | null;
  onSubmit: () => void;
}

/** Maps the shared stage resolver's selected content to the agent's real renderer. */
export const StagePage = observer(function StagePage({
  view,
  turns,
  request,
  loadingLine,
  onSubmit,
}: StagePageProps) {
  const store = useMessagingStore();
  const intl = useIntl();
  const retry = () => window.location.reload();
  let page: ReactNode;
  switch (view.kind) {
    case 'surface':
      page = (
        <SurfaceRenderer
          surfaceId={view.surfaceId}
          snapshot={view.snapshot}
          contentId={view.contentId}
          onSubmit={onSubmit}
        />
      );
      break;
    case 'process': {
      const part = store.activeProcessPart;
      const ProcessPage = part ? processPageFor(part.componentName) : undefined;
      page = ProcessPage && part ? <ProcessPage part={part} /> : null;
      break;
    }
    case 'text': {
      const turn = turns[view.turnIndex];
      const error = store.lastRunError;
      page = error && turn?.responseId === error.responseId ? (
        <ArrivalNotice
          line={{ kind: 'stuck', text: error.message, retry: true }}
          onRetry={retry}
        />
      ) : (
        <TextPage turn={turn} />
      );
      break;
    }
    case 'loading':
      page = <ArrivalNotice line={loadingLine} onRetry={retry} />;
      break;
    case 'unavailable':
      page = <p role="status">{intl.formatMessage(messages.viewCouldNotDisplay)}</p>;
      break;
  }

  return (
    <>
      {view.kind !== 'loading' && view.kind !== 'unavailable' ? (
        <UserRequestBubble request={request} />
      ) : null}
      {page}
    </>
  );
});
